/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.variant = null;

      // Get selected variant data (if variant exists).
      this.variant = this.getSelectedVariant();

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
        this.updateBestBeforeProperty();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updateBestBeforeText();
      this.updateExpectedDeliveryText();
      this.updatePickupAvailability();
      this.updateSku();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          product: this.data.product
        }
      }));
    }

    /**
     * Keeps the hidden line item property in sync with the selected variant
     */
    updateBestBeforeProperty() {
      if (!this.productForm) return;
      const inputName = 'properties[BEST BEFORE]';
      let input = this.productForm.querySelector(`input[name="${inputName}"]`);

      const rawValue = this.data?.formatted?.[this.variant?.id]?.best_before || '';
      let bestBefore = rawValue ? VariantPicker.normaliseBestBefore(rawValue) : '';
      if (!bestBefore) {
        bestBefore = rawValue.trim();
      }

      if (bestBefore) {
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = inputName;
          this.productForm.appendChild(input);
        }
        input.value = bestBefore;
      } else if (input) {
        input.remove();
      }
    }

    updateExpectedDeliveryText() {
      const expectedDeliveryEl = this.section.querySelector('.expected-delivery');
      if (!expectedDeliveryEl) return;

      const formatted = this.variant ? this.data.formatted?.[this.variant.id] : null;
      const isAvailable = formatted?.available ?? (this.variant ? this.variant.available : true);

      // Only show if out of stock
      if (isAvailable) {
        expectedDeliveryEl.textContent = '';
        expectedDeliveryEl.hidden = true;
        return;
      }

      const rawValue = formatted?.expected_delivery || '';

      let displayText = rawValue
        .replace('Expected Delivery - ', '')
        .replace('Expected Delivery:', '')
        .trim();

      if (displayText) {
        expectedDeliveryEl.textContent = displayText;
        expectedDeliveryEl.hidden = false;
      } else {
        expectedDeliveryEl.textContent = '';
        expectedDeliveryEl.hidden = true;
      }
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;
      const selectedOptions = this.getSelectedOptions();

      if (!this.variant) {
        currVariant = { options: selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === selectedOptions[i] || selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the best before text
     */

    updateBestBeforeText() {
      const bestBeforeEl = this.section.querySelector('.best-before');
      if (!bestBeforeEl) return;

      const overrideEnabled = bestBeforeEl.dataset.shelfLifeOverride === 'true';
      const overrideLabel = bestBeforeEl.dataset.overrideLabel || '';

      const formatted = this.variant ? this.data.formatted?.[this.variant.id] : null;
      const isAvailable = formatted?.available ?? (this.variant ? this.variant.available : true);

      const tags = this.data.product?.tags || [];
      const isFoodDrink = Array.isArray(tags)
        ? tags.includes('Food & Drink')
        : String(tags).indexOf('Food & Drink') !== -1;

      // Hide when not Food & Drink or when variant is out of stock
      if (!isFoodDrink || !isAvailable) {
        bestBeforeEl.textContent = '';
        bestBeforeEl.hidden = true;
        return;
      }

      const rawValue = formatted?.best_before || '';

      if (!rawValue) {
        bestBeforeEl.textContent = '';
        bestBeforeEl.hidden = true;
        return;
      }

      const cleaned = VariantPicker.normaliseBestBefore(rawValue);
      const displayText = VariantPicker.resolveBestBeforeDisplay(cleaned, rawValue, overrideEnabled, overrideLabel);

      if (displayText) {
        bestBeforeEl.textContent = displayText;
        bestBeforeEl.hidden = false;
      } else {
        bestBeforeEl.textContent = '';
        bestBeforeEl.hidden = true;
      }
    }

    static normaliseBestBefore(value) {
      if (!value) return '';
      let output = value.trim();
      const colonIndex = output.indexOf(':');
      if (colonIndex !== -1) {
        output = output.slice(colonIndex + 1).trim();
      }

      output = output.replace(/^best\s*before\s*/i,'').trim();
      return output;
    }

    static resolveBestBeforeDisplay(cleaned, rawValue, overrideEnabled, overrideLabel) {
      const baseText = rawValue || cleaned || '';
      if (!baseText) return '';

      if (!overrideEnabled) {
        return baseText;
      }

      if (!cleaned) {
        return baseText;
      }

      const matches = cleaned.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (!matches) {
        return baseText;
      }

      const day = matches[1].padStart(2, '0');
      const month = matches[2].padStart(2, '0');
      const year = matches[3];
      const parsedDate = Date.UTC(Number(year), Number(month) - 1, Number(day));
      if (Number.isNaN(parsedDate)) {
        return baseText;
      }

      const now = Date.now();
      const diffMs = parsedDate - now;
      const twelveMonthsMs = 31536000 * 1000;

      if (diffMs > twelveMonthsMs) {
        return overrideLabel || baseText;
      }

      return baseText;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;
      const unitPriceEl = this.price.querySelector('.unit-price');
      if (this.variant) {
        if (this.price.querySelector('.vat--exempt')) {
            const priceCurrentEl = this.price.querySelector('.price__current');
            const priceWasEl = this.price.querySelector('.price__was');


            // Update current price and original price if on sale.
            priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
            if (priceWasEl)
              priceWasEl.innerHTML = this.data.formatted[this.variant.id].compareAtPrice || '';
        } else {
          this.exemptPrice = this.price.querySelector('.vat--exclude')
          this.includePrice = this.price.querySelector('.vat--include')

          const priceCurrentElExe = this.exemptPrice.querySelector('.price__current');
          const priceWasElExe = this.exemptPrice.querySelector('.price__was');


          // Update current price and original price if on sale.
          priceCurrentElExe.innerHTML = this.data.formatted[this.variant.id].price;
          if (priceWasElExe)
            priceWasElExe.innerHTML = this.data.formatted[this.variant.id].compareAtPrice || '';

          const priceCurrentElInc = this.includePrice.querySelector('.price__current');
          const priceWasElInc = this.includePrice.querySelector('.price__was');


          // Update current price and original price if on sale.
          priceCurrentElInc.innerHTML = this.data.formatted[this.variant.id].priceInc;
          if (priceWasElInc)
            priceWasElInc.innerHTML = this.data.formatted[this.variant.id].compareAtPriceInc || '';
        }

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available);
      }

      this.price.querySelector('.price__default').hidden = !this.variant;
      this.price.querySelector('.price__no-variant').hidden = this.variant;
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          selectedOptions.push(selector.querySelector('.custom-select__btn').textContent.trim());
        } else {
          selectedOptions.push(selector.querySelector('input:checked').value);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
